import { useState, useCallback, useMemo } from "react";
import _ from "lodash";
import { generateRandomId } from "Utils";
import { getUniqueName } from "TranscriptsV3/modules/utils";

const useCustomModalEntities = ({
  initialEntities,
  initialTab,
  valueKey = "name",
}) => {
  const [entities, setEntities] = useState(initialEntities);
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [showErrors, setShowErrors] = useState(false);

  const entitiesViewModel = useMemo(
    () =>
      _.map(entities, row => ({
        ...row,
        hasError: showErrors && !_.trim(_.get(row, valueKey, "")),
      })),
    [entities, showErrors, valueKey]
  );

  const handleTabChange = useCallback(tab => {
    setSelectedTab(tab);
  }, []);

  const handleAddEntity = useCallback(
    (initialRow = {}) => {
      setEntities(prev => [
        ...prev,
        {
          id: generateRandomId(),
          [valueKey]: "",
          ...initialRow,
        },
      ]);
    },
    [valueKey]
  );

  const addEntityWithValue = useCallback(
    (value, initialRow = {}) => {
      if (!value) return;

      setEntities(prev => [
        ...prev,
        {
          id: generateRandomId(),
          [valueKey]: getUniqueName({
            value,
            entities: prev,
            valueKey,
          }),
          ...initialRow,
        },
      ]);
    },
    [valueKey]
  );

  const handleEntityChange = useCallback(
    (id, value) => {
      setEntities(prev =>
        _.map(prev, row =>
          row.id === id ? { ...row, [valueKey]: value } : row
        )
      );
    },
    [valueKey]
  );

  const handleEntityDelete = useCallback(id => {
    setEntities(prev => _.filter(prev, row => row.id !== id));
  }, []);

  const validateEntities = useCallback(() => {
    const hasErrors = _.some(
      entities,
      row => !_.trim(_.get(row, valueKey, ""))
    );

    setShowErrors(hasErrors);
    return hasErrors;
  }, [entities, valueKey]);

  return {
    entities,
    setEntities,
    entitiesViewModel,
    validateEntities,
    selectedTab,
    handleTabChange,
    handleAddEntity,
    addEntityWithValue,
    handleEntityChange,
    handleEntityDelete,
  };
};

export default useCustomModalEntities;
